import React, { useState } from 'react';
import cn from 'classnames';
import { IBaseElementProps } from 'tbk-components/src/components/BasicElement';
import Button, { ILink } from 'tbk-components/src/components/Button';
import Image, { IImage } from 'tbk-components/src/components/Image';
import FormLightboxButton, {
	IFormLightboxButtonProps,
} from '../FormLightboxButton';

export interface IInvestorsContactCTAHubSpotAddress {
	/**
	 * Contact CTA Address Addressee
	 */
	addressee?: string;
	/**
	 * Contact CTA Address Civic Address
	 */
	civicAddress?: string;
	/**
	 * Contact CTA Address Municipality, Province and Postal Code
	 */
	municipalityProvincePostalCode?: string;
	/**
	 * Contact CTA Address Phone Number
	 */
	phoneNumber?: string;
	/**
	 * Contact CTA Address Email
	 */
	email?: string;
}

export interface IInvestorsContactCTAHubSpotProps extends IBaseElementProps {
	children;
	/**
	 * CTA Contact Image
	 */
	contactCTAHubSpotImage?: IImage;
	/**
	 * CTA Contact Heading
	 * @control richtext
	 */
	contactCTAHubSpotHeading?: string;
	/**
	 * CTA Contact Sub Heading
	 * @control richtext
	 */
	contactCTAHubSpotSubHeading?: string;
	contactCTAHubSpotCallouts?: IFormLightboxButtonProps[];
	/**
	 * CTA Contact Additional Link - shown below the callout buttons.
	 */
	contactCTAHubSpotAdditonalLink?: ILink;
	contactCTAHubSpotAddress?: IInvestorsContactCTAHubSpotAddress;
	contactCTAHubSpotTransferAgentAddress?: IInvestorsContactCTAHubSpotAddress;
	/**
	 * CTA Contact Auditor Heading
	 */
	contactCTAHubSpotAuditorHeading?: string;
	/**
	 * CTA Contact Auditor
	 */
	contactCTAHubSpotAuditor?: ILink;
}

/**
 * Calian Investors Contact CTA HubSpot Component
 * @icon welcome-widgets-menus
 * @block
 */
const InvestorsContactCTAHubSpot: React.FC<IInvestorsContactCTAHubSpotProps> =
	({
		id,
		className,
		classNames = [],
		children,
		contactCTAHubSpotImage,
		contactCTAHubSpotHeading,
		contactCTAHubSpotSubHeading,
		contactCTAHubSpotCallouts,
		contactCTAHubSpotAdditonalLink,
		contactCTAHubSpotAddress,
		contactCTAHubSpotTransferAgentAddress,
		contactCTAHubSpotAuditor,
		contactCTAHubSpotAuditorHeading,
	}) => {
		return (
			<div
				id={id}
				className={
					className ||
					cn('calian-investors-contact-cta', ...classNames)
				}
			>
				<div className="calian-investors-contact-cta__container container-fluid">
					<div className="row">
						<div className="calian-investors-contact-cta__left col-12 col-md-6">
							<div className="container-fluid">
								<div className="row">
									{contactCTAHubSpotImage && (
										<div className="calian-investors-contact-cta__left--image col-12 col-xl-5">
											<Image
												{...contactCTAHubSpotImage}
												className="calian-investors-contact-cta--image img-fluid"
											/>
										</div>
									)}
									<div className="calian-investors-contact-cta__left--content col-12 col-xl-7">
										{contactCTAHubSpotHeading && (
											<h4
												className="calian-investors-contact-cta--heading"
												dangerouslySetInnerHTML={{
													__html: contactCTAHubSpotHeading,
												}}
											/>
										)}
										{contactCTAHubSpotSubHeading && (
											<p
												className="calian-investors-contact-cta--sub-heading"
												dangerouslySetInnerHTML={{
													__html: contactCTAHubSpotSubHeading,
												}}
											/>
										)}
										<div className="calian-investors-contact-cta__left-callouts">
											{(
												contactCTAHubSpotCallouts || []
											).map((callout, index) => {
												return (
													<FormLightboxButton
														key={index}
														{...callout}
													/>
												);
											})}
											{contactCTAHubSpotAdditonalLink?.label && (
												<Button
													className="calian-investors-contact-cta__additional-link"
													{...contactCTAHubSpotAdditonalLink}
												/>
											)}
										</div>
									</div>
								</div>
							</div>
						</div>
						<div className="calian-investors-contact-cta__right col-12 col-md-6">
							<div className="calian-investors-contact-cta__right--content">
								{contactCTAHubSpotAddress && (
									<address className="calian-investors-contact-cta__address">
										{contactCTAHubSpotAddress.addressee && (
											<p className="calian-investors-contact-cta__address--addressee">
												{
													contactCTAHubSpotAddress.addressee
												}
											</p>
										)}
										{contactCTAHubSpotAddress.civicAddress && (
											<p className="calian-investors-contact-cta__address--civic-address">
												{
													contactCTAHubSpotAddress.civicAddress
												}
											</p>
										)}
										{contactCTAHubSpotAddress.municipalityProvincePostalCode && (
											<p className="calian-investors-contact-cta__address--additional-address-details">
												{
													contactCTAHubSpotAddress.municipalityProvincePostalCode
												}
											</p>
										)}
										<p className="calian-investors-contact-cta__address--phone-email">
											{contactCTAHubSpotAddress.phoneNumber && (
												<a
													className="calian-investors-contact-cta__address--phone"
													href={
														'tel:+' +
														contactCTAHubSpotAddress.phoneNumber +
														''
													}
												>
													{
														contactCTAHubSpotAddress.phoneNumber
													}
												</a>
											)}
											{contactCTAHubSpotAddress.email && (
												<a
													className="calian-investors-contact-cta__address--email"
													href={
														'mailto:' +
														contactCTAHubSpotAddress.email +
														''
													}
												>
													{
														contactCTAHubSpotAddress.email
													}
												</a>
											)}
										</p>
									</address>
								)}
								{contactCTAHubSpotTransferAgentAddress && (
									<address className="calian-investors-contact-cta__address">
										{contactCTAHubSpotTransferAgentAddress.addressee && (
											<p className="calian-investors-contact-cta__address--addressee">
												{
													contactCTAHubSpotTransferAgentAddress.addressee
												}
											</p>
										)}
										{contactCTAHubSpotTransferAgentAddress.civicAddress && (
											<p className="calian-investors-contact-cta__address--civic-address">
												{
													contactCTAHubSpotTransferAgentAddress.civicAddress
												}
											</p>
										)}
										{contactCTAHubSpotTransferAgentAddress.municipalityProvincePostalCode && (
											<p className="calian-investors-contact-cta__address--additional-address-details">
												{
													contactCTAHubSpotTransferAgentAddress.municipalityProvincePostalCode
												}
											</p>
										)}
										<p className="calian-investors-contact-cta__address--phone-email">
											{contactCTAHubSpotTransferAgentAddress.phoneNumber && (
												<a
													className="calian-investors-contact-cta__address--phone"
													href={
														'tel:+' +
														contactCTAHubSpotTransferAgentAddress.phoneNumber +
														''
													}
												>
													{
														contactCTAHubSpotTransferAgentAddress.phoneNumber
													}
												</a>
											)}
											{contactCTAHubSpotTransferAgentAddress.email && (
												<a
													className="calian-investors-contact-cta__address--email"
													href={
														'mailto:' +
														contactCTAHubSpotTransferAgentAddress.email +
														''
													}
												>
													{
														contactCTAHubSpotTransferAgentAddress.email
													}
												</a>
											)}
										</p>
									</address>
								)}
								{contactCTAHubSpotAuditor?.label && (
									<div className="calian-investors-contact-cta__right--auditors">
										<p className="calian-investors-contact-cta__right--auditors-title">
											{contactCTAHubSpotAuditorHeading}
										</p>
										{contactCTAHubSpotAuditor?.label && (
											<Button
												className="calian-investors-contact-cta__right--auditors-title"
												{...contactCTAHubSpotAuditor}
											/>
										)}
									</div>
								)}
							</div>
						</div>
					</div>
				</div>
			</div>
		);
	};

export default InvestorsContactCTAHubSpot;
